import { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { PORTAL_KEYS } from '../contexts/MarketContext';
import {
  GOAL_SETTINGS_PATH, aggregateGoals, goalTotals, goalsForPortals, monthsElapsed, normalizeGoalConfig,
} from '../constants/goals';

/**
 * The admin-editable season + goals (`goalSettings/current`), normalised, with
 * the built-in defaults until an admin first saves. `raw` is the stored doc
 * (or null) for the editor to start from.
 */
export function useGoalConfig() {
  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, ...GOAL_SETTINGS_PATH),
      (snap) => {
        setRaw(snap.exists() ? snap.data() : null);
        setLoading(false);
      },
      (err) => {
        console.error('Goal settings subscription failed', err);
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  const config = useMemo(() => normalizeGoalConfig(raw), [raw]);
  return { config, raw, loading };
}

/**
 * Live season-goal progress for every portal the viewer can access.
 *
 * Deliberately keyed off the user's portal access rather than the shell they
 * happen to be in: someone who runs several portals should see all of their
 * goals wherever they land after signing in.
 *
 * Shared by the Goals page and the login splash so the two can never disagree
 * about what counts. Both read the same `requests` subscription.
 */
export default function useGoalProgress() {
  const { userPortals } = useAuth();
  const { config, raw, loading: configLoading } = useGoalConfig();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'requests'),
      (snap) => {
        setRequests(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('Goal progress subscription failed', err);
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  const portals = userPortals && userPortals.length ? userPortals : PORTAL_KEYS;
  const portalsKey = portals.join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const goals = useMemo(() => goalsForPortals(portals, config.goals), [portalsKey, config]);
  const elapsed = useMemo(() => monthsElapsed(new Date(), config.months), [config]);
  const data = useMemo(
    () => aggregateGoals(requests, goals, elapsed, config.months),
    [requests, goals, elapsed, config]
  );
  const totals = useMemo(() => goalTotals(goals, data), [goals, data]);

  return {
    goals, data, totals, elapsed, portals,
    season: config.season, months: config.months, config, rawConfig: raw,
    loading: loading || configLoading,
  };
}
